import { themeVars } from '@lemonade-hq/uniclient-theme';
import type { Variants } from 'motion/react';
import { completedHeight, getDotCurrentFadeDelay, getLineProgressDelay, getTextFadeDelay } from './animationHelpers';
import {
  COMPLETED_ANIMATION_DURATION,
  FADE_IN_DURATION,
  FADE_OUT_DURATION,
  LEVEL_ONE_ANIMATION_DELAY,
  LINE_ANIMATION_DURATION,
  SUB_ITEMS_OPACITY_TRANSITION_DURATION,
  SUB_ITEMS_WRAPPER_EXPANSION_DURATION,
  TEXT_COLOR_TRANSITION_DURATION,
  TEXT_PINK_HOLD_DURATION,
  TIME_TO_MAKE_ANIMATION_LOOK_GOOD,
} from './constants';
import type { AnimationProgressProps, SubItemDotProps } from './types';

export const progressSubItemsWrapperVariants: Variants = {
  initial: {
    opacity: 0,
    height: 0,
    transition: {
      duration: SUB_ITEMS_WRAPPER_EXPANSION_DURATION,
      ease: 'easeOut',
    },
  },
  animate: {
    opacity: 1,
    height: 'auto',
    transition: {
      height: {
        duration: SUB_ITEMS_WRAPPER_EXPANSION_DURATION,
        ease: 'easeOut',
      },
      opacity: {
        duration: 0,
        delay: SUB_ITEMS_WRAPPER_EXPANSION_DURATION,
      },
    },
  },
  exit: {
    opacity: 0,
    height: 0,
    transition: {
      opacity: {
        duration: SUB_ITEMS_OPACITY_TRANSITION_DURATION,
      },
      height: {
        duration: SUB_ITEMS_WRAPPER_EXPANSION_DURATION,
        ease: 'easeOut',
        delay: LINE_ANIMATION_DURATION,
      },
    },
  },
};

// Handles the extra item appearance
export const itemContainerVariants: Variants = {
  initial: { opacity: 0 },
  animate: {
    opacity: 1,
    transition: { duration: FADE_IN_DURATION },
  },
  exit: {
    opacity: 0,
    transition: { duration: FADE_OUT_DURATION },
  },
};

export const itemContentWrapperVariants: Variants = {
  initial: { opacity: 0 },
  animate: {
    opacity: 1,
    transition: { delay: SUB_ITEMS_WRAPPER_EXPANSION_DURATION },
  },
};

export const outerDotVariants: Variants = {
  open: {
    scale: 1,
    backgroundColor: themeVars.color.frameProgressInactive,
  },
  closed: {
    scale: 1,
    backgroundColor: themeVars.color.frameProgressInactive,
  },
  currentFade: ({ isBackwards }: { readonly isBackwards: boolean }) => ({
    backgroundColor: themeVars.color.frameProgressActive,
    transition: {
      delay: isBackwards ? 0 : LINE_ANIMATION_DURATION,
      from: themeVars.color.textSecondary,
    },
  }),
  completedFade: {
    backgroundColor: [themeVars.color.frameProgressActive, themeVars.color.frameProgressInactive],
    transition: {
      delay: LEVEL_ONE_ANIMATION_DELAY,
    },
  },
};

export const innerDotVariants: Variants = {
  open: {
    scale: 1,
    opacity: 1,
  },
  closed: {
    scale: 0,
    opacity: 0,
  },
  currentFade: {
    scale: 1,
    opacity: 1,
    transition: {
      delay: 0.5,
    },
  },
  completedFade: {
    scale: 0,
    opacity: 0,
    transition: {
      delay: 0.5,
    },
  },
};

export const subItemDotVariants = ({
  isFirst,
  previousSubItemHasSubSubItems,
  isBackwards,
}: SubItemDotProps): Variants => ({
  initial: {
    scale: 0,
  },
  open: {
    scale: 1,
    backgroundColor: themeVars.color.frameProgressLine,
    transition: {
      duration: SUB_ITEMS_OPACITY_TRANSITION_DURATION,
      delay: isBackwards ? 0 : SUB_ITEMS_WRAPPER_EXPANSION_DURATION,
    },
  },
  closed: {
    scale: 1,
    backgroundColor: themeVars.color.frameProgressActive,
  },
  currentFade: {
    backgroundColor: themeVars.color.frameProgressActive,
    transition: {
      delay: getDotCurrentFadeDelay({ isBackwards, previousSubItemHasSubSubItems, isFirst }),
    },
  },
  completedFade: {
    scale: 1,
    backgroundColor: themeVars.color.frameProgressActive,
  },
});

export const lineVariants = {
  initial: {
    zIndex: 1,
    height: 0,
    backgroundColor: themeVars.color.frameProgressLine,
  },
  progress: (props: AnimationProgressProps) => ({
    backgroundColor: [themeVars.color.frameProgressActive],
    zIndex: 2,
    height: completedHeight(props),
    transition: {
      duration: LINE_ANIMATION_DURATION,
      delay: getLineProgressDelay(props),
    },
  }),
  completed: {
    backgroundColor: [
      themeVars.color.frameProgressActive,
      themeVars.color.frameProgressActive,
      themeVars.color.frameProgressActive,
      themeVars.color.frameProgressLine,
      themeVars.color.frameProgressLine,
    ],
    zIndex: 2,
    height: ['0%', '100%', '100%', '100%', '100%', '0%'],
    borderBottomLeftRadius: ['100px', '0px', '0px', '0px', '100px'],
    borderBottomRightRadius: ['100px', '0px', '0px', '0px', '100px'],
    transition: {
      delay: SUB_ITEMS_WRAPPER_EXPANSION_DURATION,
      duration: COMPLETED_ANIMATION_DURATION,
    },
  },
  completedFade: (props: AnimationProgressProps) => ({
    backgroundColor: themeVars.color.frameProgressActive,
    height: completedHeight(props),
    transition: {
      delay: SUB_ITEMS_WRAPPER_EXPANSION_DURATION,
      duration: SUB_ITEMS_OPACITY_TRANSITION_DURATION,
    },
  }),
};

export const textVariants = ({
  level,
  isExpanding,
  previousSubItemHasSubSubItems,
  isBackwards,
}: Pick<AnimationProgressProps, 'isBackwards' | 'isExpanding' | 'level'> & {
  readonly previousSubItemHasSubSubItems?: boolean;
}): Variants => {
  const color = level === 1 ? themeVars.color.textPrimary : themeVars.color.textTertiary;
  const backwardsColor = isBackwards ? themeVars.color.textPrimary : themeVars.color.textTertiary;

  return {
    open: {
      color: level === 1 ? themeVars.color.textPrimary : backwardsColor,
      transition: {
        delay: isExpanding ? SUB_ITEMS_WRAPPER_EXPANSION_DURATION - TIME_TO_MAKE_ANIMATION_LOOK_GOOD : 0,
      },
    },
    closed: {
      color,
      transition: {
        delay: LINE_ANIMATION_DURATION - TIME_TO_MAKE_ANIMATION_LOOK_GOOD,
      },
    },
    currentFade: {
      color: [
        level === 1 ? themeVars.color.textPrimary : themeVars.color.textTertiary,
        themeVars.color.textLink,
        themeVars.color.textPrimary,
      ],
      transition: {
        delay: isBackwards ? 0 : getTextFadeDelay({ level, previousSubItemHasSubSubItems, isExpanding, isBackwards }),
        duration: isBackwards ? 0 : TEXT_COLOR_TRANSITION_DURATION,
        times: [0, TEXT_PINK_HOLD_DURATION, 1],
      },
    },
    completedFade: {
      color,
    },
  };
};
